import { Component, OnInit } from '@angular/core';
import { Chord, Note } from '@tonaljs/tonal';

const STANDARD_TUNING = ['E', 'A', 'D', 'G', 'B', 'E'];
const NOTE_SEQUENCE = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];

const SCALE_TYPES = [
  { label: 'Major',         suffix: 'major',      intervals: [0, 2, 4, 5, 7, 9, 11] },
  { label: 'Natural Minor', suffix: 'minor',      intervals: [0, 2, 3, 5, 7, 8, 10] },
  { label: 'Pent. Major',   suffix: 'pent_maj',   intervals: [0, 2, 4, 7, 9] },
  { label: 'Pent. Minor',   suffix: 'pent_min',   intervals: [0, 3, 5, 7, 10] },
  { label: 'Blues',         suffix: 'blues',      intervals: [0, 3, 5, 6, 7, 10] },
  { label: 'Dorian',        suffix: 'dorian',     intervals: [0, 2, 3, 5, 7, 9, 10] },
  { label: 'Mixolydian',    suffix: 'mixolydian', intervals: [0, 2, 4, 5, 7, 9, 10] },
];

const SCALE_POSITION_WINDOWS = [
  { label: 'Open',  minFret: 0,  maxFret: 4  },
  { label: 'Pos 2', minFret: 2,  maxFret: 6  },
  { label: 'Pos 3', minFret: 5,  maxFret: 9  },
  { label: 'Pos 4', minFret: 7,  maxFret: 11 },
  { label: 'Pos 5', minFret: 9,  maxFret: 12 },
];

function getNoteForFret(openNote: string, fret: number): string {
  const idx = NOTE_SEQUENCE.indexOf(openNote);
  return NOTE_SEQUENCE[(idx + fret) % 12];
}

function normalizeNote(note: string): string {
  // Use chroma (pitch class 0–11) to handle any enharmonic spelling:
  // E#→F, B#→C, F##→G, Bb→A#, etc.
  const chroma = Note.get(note).chroma;
  return chroma !== undefined ? NOTE_SEQUENCE[chroma] : note;
}

const ROOT_NOTES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
const CHORD_VARIANTS = [
  { label: 'Major', suffix: '' },
  { label: 'Minor', suffix: 'm' },
  { label: '7',     suffix: '7' },
  { label: 'm7',    suffix: 'm7' },
  { label: 'maj7',  suffix: 'maj7' },
  { label: 'sus2',  suffix: 'sus2' },
  { label: 'sus4',  suffix: 'sus4' },
  { label: 'dim',   suffix: 'dim' },
  { label: 'aug',   suffix: 'aug' },
  { label: 'add9',  suffix: 'add9' },
];

@Component({
    selector: 'app-guitar-visualizer',
    templateUrl: './visualizer.component.html',
    styleUrls: ['./visualizer.component.scss'],
    standalone: false
})
export class GuitarVisualizerComponent implements OnInit {
  // Display order: high E at index 0, low E at index 5
  strings = [...STANDARD_TUNING].reverse();
  frets = Array.from({ length: 13 }, (_, i) => i);
  mode: 'notes' | 'chords' | 'scales' | 'intervals' = 'chords';
  highlighted: { [key: string]: boolean } = {};

  rootNotes = ROOT_NOTES;
  chordVariants = CHORD_VARIANTS;
  selectedRoot = ROOT_NOTES[0];
  selectedVariant = '';

  allVoicings: (number | null)[][] = [];
  coreVoicings: (number | null)[][] = [];
  showAllVoicings = false;
  currentVoicingIndex = 0;

  // Scales mode
  scaleTypes = SCALE_TYPES;
  selectedScaleRoot = ROOT_NOTES[0];
  selectedScaleType = 'major';
  showScaleDegrees = false;
  showAllScaleNotes = true;
  currentScalePositionIndex = 0;
  scaleNoteInfo: { [key: string]: { note: string; degree: number; isRoot: boolean } } = {};

  get totalScalePositions(): number { return SCALE_POSITION_WINDOWS.length; }
  get currentScalePositionNumber(): number { return this.currentScalePositionIndex + 1; }
  get scalePositionLabel(): string { return SCALE_POSITION_WINDOWS[this.currentScalePositionIndex]?.label ?? ''; }

  get activeVoicings(): (number | null)[][] {
    return this.showAllVoicings ? this.allVoicings : this.coreVoicings;
  }
  get totalVoicings(): number { return this.activeVoicings.length; }
  get currentVoicingNumber(): number { return this.activeVoicings.length ? this.currentVoicingIndex + 1 : 0; }
  get voicingPositionLabel(): string {
    const v = this.activeVoicings[this.currentVoicingIndex];
    if (!v) return '';
    const nonOpen = v.filter(f => f !== null && f > 0) as number[];
    return nonOpen.length ? `Fret ${Math.min(...nonOpen)}` : 'Open';
  }

  ngOnInit() {
    this.selectChord();
  }

  onRootChange(root: string) {
    this.selectedRoot = root;
    this.selectChord();
  }

  onVariantChange(variant: string) {
    this.selectedVariant = variant;
    this.selectChord();
  }

  selectChord() {
    this.allVoicings = this.computeVoicings(this.selectedRoot + this.selectedVariant);
    this.coreVoicings = this.deduplicateByCoreShape(this.allVoicings);
    this.currentVoicingIndex = 0;
    this.displayCurrentVoicing();
  }

  setVoicingMode(showAll: boolean) {
    this.showAllVoicings = showAll;
    this.currentVoicingIndex = 0;
    this.displayCurrentVoicing();
  }

  prevVoicing() {
    if (this.currentVoicingIndex > 0) {
      this.currentVoicingIndex--;
      this.displayCurrentVoicing();
    }
  }

  nextVoicing() {
    if (this.currentVoicingIndex < this.activeVoicings.length - 1) {
      this.currentVoicingIndex++;
      this.displayCurrentVoicing();
    }
  }

  private displayCurrentVoicing() {
    this.highlighted = {};
    const voicing = this.activeVoicings[this.currentVoicingIndex];
    if (!voicing) return;
    voicing.forEach((fret, stringIdx) => {
      if (fret !== null) {
        this.highlighted[`${stringIdx}-${fret}`] = true;
      }
    });
  }

  // Group voicings by their first 5 strings (everything except low E).
  // Within each group, keep the voicing with the most strings played.
  // This collapses variants that differ only in whether low E is open/muted/fretted.
  private deduplicateByCoreShape(voicings: (number | null)[][]): (number | null)[][] {
    const groups = new Map<string, (number | null)[]>();
    for (const v of voicings) {
      const key = JSON.stringify(v.slice(0, 5));
      const existing = groups.get(key);
      if (!existing) {
        groups.set(key, v);
      } else {
        const played = (arr: (number | null)[]) => arr.filter(f => f !== null).length;
        if (played(v) > played(existing)) {
          groups.set(key, v);
        }
      }
    }
    return Array.from(groups.values());
  }

  private computeVoicings(chordName: string): (number | null)[][] {
    const chordInfo = Chord.get(chordName);
    if (!chordInfo.notes?.length) return [];
    const chordNoteSet = new Set(chordInfo.notes.map(normalizeNote));

    // Per-string: which frets (0–12) produce a chord note?
    const optsByString = this.strings.map(openNote =>
      Array.from({ length: 13 }, (_, f) => f).filter(f =>
        chordNoteSet.has(getNoteForFret(openNote, f))
      )
    );

    const valid: (number | null)[][] = [];
    const strings = this.strings;

    // Recursive backtracking over strings.
    // lo/hi track the min/max of non-open (>0) frets chosen so far.
    // Open strings (fret 0) don't constrain the window.
    const gen = (idx: number, curr: (number | null)[], lo: number, hi: number) => {
      if (idx === 6) {
        const played = curr.filter(f => f !== null) as number[];
        if (played.length < 4) return;

        // No interior mutes: muted strings must only trail at the bass end
        const firstPlayed = curr.findIndex(f => f !== null);
        const lastPlayed = 5 - [...curr].reverse().findIndex(f => f !== null);
        for (let i = firstPlayed; i <= lastPlayed; i++) {
          if (curr[i] === null) return;
        }

        // All chord notes must appear in this voicing
        const voicingNotes = new Set(
          curr.map((f, i) => f !== null ? getNoteForFret(strings[i], f) : null)
              .filter(n => n !== null) as string[]
        );
        if ([...chordNoteSet].every(n => voicingNotes.has(n))) {
          valid.push([...curr]);
        }
        return;
      }

      // Muting is only allowed for the last two strings (A, low E — indices 4 and 5)
      if (idx >= 4) {
        gen(idx + 1, [...curr, null], lo, hi);
      }

      for (const fret of optsByString[idx]) {
        if (fret === 0) {
          gen(idx + 1, [...curr, 0], lo, hi);
        } else {
          const newLo = lo > fret ? fret : lo;
          const newHi = hi < fret ? fret : hi;
          if (newHi - newLo > 4) continue; // Span too wide — prune
          gen(idx + 1, [...curr, fret], newLo, newHi);
        }
      }
    };

    // Start with lo=99 (no non-open frets yet), hi=0
    gen(0, [], 99, 0);

    // Sort: open/low-position voicings first, then ascending up the neck
    return valid.sort((a, b) => {
      const nonOpenA = a.filter(f => f !== null && f > 0) as number[];
      const nonOpenB = b.filter(f => f !== null && f > 0) as number[];
      const minA = nonOpenA.length ? Math.min(...nonOpenA) : 0;
      const minB = nonOpenB.length ? Math.min(...nonOpenB) : 0;
      return minA - minB;
    });
  }

  // --- Notes mode: free-click to highlight cells and detect chord ---
  getFretboardNotes(): { [key: string]: string } {
    const notes: { [key: string]: string } = {};
    this.strings.forEach((openNote, si) => {
      this.frets.forEach(fret => {
        notes[`${si}-${fret}`] = getNoteForFret(openNote, fret);
      });
    });
    return notes;
  }

  onCellClick(stringIdx: number, fret: number) {
    if (this.mode !== 'notes') return;
    const key = `${stringIdx}-${fret}`;
    this.highlighted[key] = !this.highlighted[key];
  }

  setMode(mode: 'notes' | 'chords' | 'scales' | 'intervals') {
    this.mode = mode;
    this.highlighted = {};
    if (mode === 'chords') this.selectChord();
    if (mode === 'scales') this.displayScale();
  }

  // --- Scale mode ---
  onScaleRootChange(root: string) {
    this.selectedScaleRoot = root;
    this.displayScale();
  }

  onScaleTypeChange(type: string) {
    this.selectedScaleType = type;
    this.displayScale();
  }

  setScaleLabelMode(showDegrees: boolean) {
    this.showScaleDegrees = showDegrees;
  }

  setScaleViewMode(showAll: boolean) {
    this.showAllScaleNotes = showAll;
    this.currentScalePositionIndex = 0;
    this.displayScale();
  }

  prevScalePosition() {
    if (this.currentScalePositionIndex > 0) {
      this.currentScalePositionIndex--;
      this.displayScale();
    }
  }

  nextScalePosition() {
    if (this.currentScalePositionIndex < SCALE_POSITION_WINDOWS.length - 1) {
      this.currentScalePositionIndex++;
      this.displayScale();
    }
  }

  goToScale() {
    this.selectedScaleRoot = this.selectedRoot;
    this.setMode('scales');
  }

  private displayScale() {
    const scale = SCALE_TYPES.find(s => s.suffix === this.selectedScaleType);
    if (!scale) return;

    const rootIdx = NOTE_SEQUENCE.indexOf(this.selectedScaleRoot);
    const noteToDegree = new Map<string, number>();
    scale.intervals.forEach((interval, i) => {
      noteToDegree.set(NOTE_SEQUENCE[(rootIdx + interval) % 12], i + 1);
    });

    const win = this.showAllScaleNotes ? null : SCALE_POSITION_WINDOWS[this.currentScalePositionIndex];
    this.highlighted = {};
    this.scaleNoteInfo = {};

    this.strings.forEach((openNote, si) => {
      this.frets.forEach(fret => {
        if (win && (fret < win.minFret || fret > win.maxFret)) return;
        const note = getNoteForFret(openNote, fret);
        if (noteToDegree.has(note)) {
          const key = `${si}-${fret}`;
          const degree = noteToDegree.get(note)!;
          this.highlighted[key] = true;
          this.scaleNoteInfo[key] = { note, degree, isRoot: degree === 1 };
        }
      });
    });
  }

  getSelectedNotes(): string[] {
    const fretboardNotes = this.getFretboardNotes();
    return Array.from(new Set(
      Object.keys(this.highlighted)
        .filter(k => this.highlighted[k])
        .map(k => fretboardNotes[k])
    ));
  }

  detectChord(): string | null {
    const selected = this.getSelectedNotes().sort();
    if (selected.length < 3) return null;
    const names = Chord.detect(selected);
    return names.length ? names[0] : null;
  }
}
